import React, { useState } from 'react';
import classnames from 'classnames';
import { forwardRef } from '@heroui/system';
import { CustomAvatarProps } from '../Avatar/Avatar';

export type CustomAvatarGroupProps = React.HTMLAttributes<HTMLDivElement> & {
  size?: CustomAvatarProps['size'];
  radius?: CustomAvatarProps['radius'];
  color?: CustomAvatarProps['color'];
  isBordered?: boolean;
  max?: number;
  total?: number;
  renderCount?: (count: number) => React.ReactNode;
};

const AvatarGroupWrapper = forwardRef<'div', CustomAvatarGroupProps>((props, ref) => {
  const {
    children,
    size,
    radius,
    color,
    isBordered = true,
    max = 5,
    total,
    renderCount = (count) => <p className="text-m-r text-base-10 ms-1">+{count} others</p>,
    className,
    ...restProps
  } = props;

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const childrenArray = React.Children.toArray(children).filter(React.isValidElement);
  const visibleChildren = childrenArray.slice(0, max);

  const totalCount = total ?? childrenArray.length;
  const hiddenCount = Math.max(totalCount - visibleChildren.length, 0);

  return (
    <div ref={ref} className={classnames('flex items-center shrink-0 overflow-visible', className)} {...restProps}>
      {visibleChildren.map((child, index) => (
        <div
          key={(child as React.ReactElement).key ?? index}
          onMouseEnter={() => setHoveredIndex(index)}
          onMouseLeave={() => setHoveredIndex(null)}
          onFocus={() => setHoveredIndex(index)}
          onBlur={() => setHoveredIndex(null)}
          className={classnames('relative shrink-0 overflow-visible transition-transform duration-150 ease-out', {
            'scale-105': hoveredIndex === index,
            '-ms-2': index > 0,
          })}
        >
          {React.isValidElement<CustomAvatarProps>(child)
            ? React.cloneElement(child, {
                size,
                radius,
                color,
                isBordered,
              })
            : child}
        </div>
      ))}

      {hiddenCount > 0 && renderCount(hiddenCount)}
    </div>
  );
});

AvatarGroupWrapper.displayName = 'AvatarGroupWrapper';

export default AvatarGroupWrapper;
